import React, { useState, useMemo, useEffect } from "react";
import {
    UserPlus,
    Flame,
    UserX,
    Search
} from "lucide-react";
import "./ActivityManager.css";
import api from '../api/axiosConfig';
import Modal from "./Modal";
import Progress from "./Progress";

const ActivityManager = () => {
    const [activeTab, setActiveTab] = useState("recent"); // 'recent' | 'active' | 'inactive'
    const [searchTerm, setSearchTerm] = useState("");
    const [studentsData, setStudentsData] = useState({ recent: [], active: [], inactive: [] });
    const [sendingInProgress, setSendingInProgress] = useState(false);

    useEffect(() => {
        fetchActivityData();
    }, []);

    const fetchActivityData = async () => {
        try {
            const { data } = await api.get('/admin/activity'); // Make sure you have a GET route in your backend!
            setStudentsData(data['data']);
        } catch (err) { console.error("Error fetching dashboard data"); }
    };

    const triggerReminderEmail = async (studentId) => {
        setSendingInProgress(true);
        try {
            const { data } = await api.post('/admin/new-parent-reminder', { id: studentId });
            console.log(data);
        } catch (err) {
            console.error('Error triggering reminder email', err);
        } finally {
            setSendingInProgress(false);
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return "Never";
        const d = new Date(isoString);
        return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    const getDaysInactive = (isoString) => {
        if (!isoString) return 999;
        const diffTime = Math.abs(new Date() - new Date(isoString));
        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    };

    // 1. Recently Registered (Sorted descending by joinedAt)
    const recentStudents = useMemo(() => {
        return [...studentsData.recent].sort(
            (a, b) => new Date(b.joinedAt) - new Date(a.joinedAt)
        );
    }, [studentsData]);

    // 2. Most Active (Sorted descending by attemptsCount, then points)
    const mostActiveStudents = useMemo(() => {
        return [...studentsData.active].sort((a, b) => {
            if (b.attemptsCount !== a.attemptsCount) {
                return b.attemptsCount - a.attemptsCount;
            }
            return b.points - a.points;
        });
    }, [studentsData]);

    // 3. Oldest Inactive (Sorted ascending by joinedAt)
    const inactiveStudents = useMemo(() => {
        return [...studentsData.inactive]
            .filter((s) => getDaysInactive(s.lastActiveAt) > 30 || s.attemptsCount < 10)
            .sort((a, b) => new Date(a.joinedAt) - new Date(b.joinedAt));
    }, [studentsData]);

    const currentDataset = useMemo(() => {
        let list = [];
        if (activeTab === "recent") list = recentStudents;
        if (activeTab === "active") list = mostActiveStudents;
        if (activeTab === "inactive") list = inactiveStudents;

        if (!searchTerm.trim()) return list;

        const term = searchTerm.toLowerCase();
        return list.filter(
            (s) =>
                s.name.toLowerCase().includes(term) ||
                s.parentEmail.toLowerCase().includes(term) ||
                s.grade.toLowerCase().includes(term)
        );
    }, [activeTab, recentStudents, mostActiveStudents, inactiveStudents, searchTerm]);

    return (
        <div className="activity-page">
            {/* Top Header */}
            <div className="activity-header">
                <div>
                    <h1 className="activity-title">User Activity</h1>
                    <p className="activity-subtitle">
                        Manage registrations, track top learners, and review inactive accounts.
                    </p>
                </div>

                <div className="search-box">
                    <Search size={16} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search students, emails..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {/* Metrics Row */}
            <div className="stats-row">
                <div className="stat-box">
                    <div className="stat-icon-wrapper stat-green">
                        <UserPlus size={20} />
                    </div>
                    <div>
                        <div className="stat-label">Recently Joined</div>
                        <div className="stat-value">{recentStudents.length}</div>
                        <div className="stat-desc">Past 30 days</div>
                    </div>
                </div>

                <div className="stat-box">
                    <div className="stat-icon-wrapper stat-orange">
                        <Flame size={20} />
                    </div>
                    <div>
                        <div className="stat-label">Power Quiz Takers</div>
                        <div className="stat-value">
                            {mostActiveStudents.filter((s) => s.attemptsCount >= 20).length}
                        </div>
                        <div className="stat-desc">20+ quizzes logged</div>
                    </div>
                </div>

                <div className="stat-box">
                    <div className="stat-icon-wrapper stat-red">
                        <UserX size={20} />
                    </div>
                    <div>
                        <div className="stat-label">Inactive Students</div>
                        <div className="stat-value">{inactiveStudents.length}</div>
                        <div className="stat-desc">Over 30 days idle</div>
                    </div>
                </div>
            </div>

            {/* Actual Tabs (Underline/Folder style) */}
            <div className="tabs-container">
                <div className="tabs-nav">
                    <button
                        type="button"
                        className={`tab-item ${activeTab === "recent" ? "tab-active" : ""}`}
                        onClick={() => setActiveTab("recent")}
                    >
                        <UserPlus size={16} />
                        <span>Recently Registered</span>
                        <span className="tab-count">{recentStudents.length}</span>
                    </button>

                    <button
                        type="button"
                        className={`tab-item ${activeTab === "active" ? "tab-active" : ""}`}
                        onClick={() => setActiveTab("active")}
                    >
                        <Flame size={16} />
                        <span>Most Active</span>
                        <span className="tab-count">{mostActiveStudents.length}</span>
                    </button>

                    <button
                        type="button"
                        className={`tab-item ${activeTab === "inactive" ? "tab-active" : ""}`}
                        onClick={() => setActiveTab("inactive")}
                    >
                        <UserX size={16} />
                        <span>Oldest Inactive</span>
                        <span className="tab-count count-warning">{inactiveStudents.length}</span>
                    </button>
                </div>
            </div>

            {/* Solid Data Table Card */}
            <div className="table-card">
                <div className="table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Grade</th>
                                <th>Parent Email</th>
                                {activeTab === "recent" && <th>Date Joined</th>}
                                {activeTab === "active" && <th>Quiz Runs</th>}
                                {activeTab === "active" && <th>Accuracy</th>}
                                {activeTab === "inactive" && <th>Joined On</th>}
                                {activeTab === "inactive" && <th>Inactive For</th>}
                                <th>Total Points</th>
                                <th className="align-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentDataset.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="no-data">
                                        No students match your search.
                                    </td>
                                </tr>
                            ) : (
                                currentDataset.map((student) => {
                                    const daysQuiet = getDaysInactive(student.lastActiveAt);
                                    return (
                                        <tr key={student.id}>
                                            {/* Student Info */}
                                            <td>
                                                <div className="profile-cell">
                                                    <div>
                                                        <div className="student-title">{student.name}</div>
                                                        <div className="student-id">ID: #{student.id}</div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Grade */}
                                            <td>
                                                <span className="grade-tag">
                                                    {student.grade}
                                                </span>
                                            </td>

                                            {/* Parent Email */}
                                            <td>
                                                <a
                                                    href={`mailto:${student.parentEmail}`}
                                                    className="email-link"
                                                    title="Send email"
                                                >
                                                    {student.parentEmail}
                                                </a>
                                            </td>

                                            {/* Tab 1: Date Joined */}
                                            {activeTab === "recent" && (
                                                <td>
                                                    <div className="date-text">
                                                        {formatDate(student.joinedAt)}
                                                    </div>
                                                </td>
                                            )}

                                            {/* Tab 2: Attempts */}
                                            {activeTab === "active" && (
                                                <td>
                                                    <div className="attempts-text">
                                                        <span>{student.attemptsCount} quizzes</span>
                                                    </div>
                                                </td>
                                            )}

                                            {activeTab === "active" && (
                                                <td>
                                                    <span className="accuracy-badge">
                                                        {student.accuracy}%
                                                    </span>
                                                </td>
                                            )}

                                            {/* Tab 3: Joined Date */}
                                            {activeTab === "inactive" && (
                                                <td>
                                                    <div className="date-text">
                                                        {formatDate(student.joinedAt)}
                                                    </div>
                                                </td>
                                            )}

                                            {activeTab === "inactive" && (
                                                <td>
                                                    <span className="warning-tag">
                                                        {daysQuiet} days
                                                    </span>
                                                </td>
                                            )}

                                            {/* Points */}
                                            <td>
                                                <div className="points-badge">
                                                    <span>{student.points.toLocaleString()} XP</span>
                                                </div>
                                            </td>

                                            {/* Action */}
                                            <td className="align-right action-area">
                                                <button
                                                    type="button"
                                                    className="action-button"
                                                    onClick={() => triggerReminderEmail(student.id)}
                                                >
                                                    <span>Remind</span>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <Modal isOpen={sendingInProgress} title="Wait">
                <Progress message="Please wait..." subtext="Notifying user." />
            </Modal>
        </div>
    );
};

export default ActivityManager;